/**
 * ApiKeyUsageChart Component
 *
 * Displays usage statistics for a single API key or all keys in a pool.
 * Shows quota usage, error counts, and relative distribution bars.
 */

import React from 'react';
import { clsx } from 'clsx';
import type { ApiKeyUsageItem } from '../../../services/llmAdminApi';

export interface ApiKeyUsageChartProps {
  keys: ApiKeyUsageItem[];
  title?: string;
  className?: string;
}

const UsageBar: React.FC<{
  value: number;
  max: number;
  color: string;
  label: string;
}> = ({ value, max, color, label }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-600">{label}</span>
        <span className="text-gray-800 font-medium">{value.toLocaleString()}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={clsx('h-full rounded-full transition-all', color)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export const ApiKeyUsageChart: React.FC<ApiKeyUsageChartProps> = ({
  keys,
  title,
  className = '',
}) => {
  if (keys.length === 0) {
    return (
      <div className={clsx('text-center py-4 text-gray-500 text-sm', className)}>
        暂无使用数据
      </div>
    );
  }

  const maxQuota = Math.max(...keys.map((k) => k.used_quota), 1);
  const maxErrors = Math.max(...keys.map((k) => k.error_count), 1);
  const totalQuota = keys.reduce((sum, k) => sum + k.used_quota, 0);
  const totalErrors = keys.reduce((sum, k) => sum + k.error_count, 0);

  return (
    <div className={clsx('space-y-4', className)}>
      {title && (
        <h4 className="text-sm font-medium text-gray-700">{title}</h4>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="p-3 bg-blue-50 rounded-lg">
          <div className="text-xs text-gray-500">总已用配额</div>
          <div className="text-lg font-semibold text-blue-700">
            {totalQuota.toLocaleString()}
          </div>
        </div>
        <div className="p-3 bg-red-50 rounded-lg">
          <div className="text-xs text-gray-500">总错误数</div>
          <div className="text-lg font-semibold text-red-700">
            {totalErrors.toLocaleString()}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {keys.map((key) => (
          <div
            key={key.key_index}
            className="p-3 bg-gray-50 rounded-lg space-y-2"
          >
            <div className="text-xs font-mono text-gray-600">
              Key #{key.key_index}: {key.key_preview}
            </div>
            <UsageBar
              value={key.used_quota}
              max={maxQuota}
              color="bg-blue-400"
              label="已用配额"
            />
            <UsageBar
              value={key.error_count}
              max={maxErrors}
              color="bg-red-400"
              label="错误数"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ApiKeyUsageChart;
